import { useCallback, useState } from 'react'
import {
  evaluateTransferEligibility,
  type TransferEligibilityResult,
} from '@shared/calls/transfer-eligibility'
import type { ChatUser } from '../../../types/chat'
import type { CallTransferMode, VoiceCallSession } from '../../../types/voice-call'
import { useVoiceCall } from '../../../contexts/voice-call-context'
import { getLocationProvider } from '../../sessions/location/location-provider'
import {
  getVoiceCallSession,
  patchVoiceCallSession,
} from '../../../stores/voice-call-store'
import { bookCallbackSession } from '../data/callback-session-client'
import { getTransferEligibilityConfig } from '../data/transfer-config'
import { buildTransferTargetContext } from '../utils/transfer-target-context'
import type { TransferEligibilityChoice } from '../components/transfer-eligibility-dialog'

function callPeerUserId(session: VoiceCallSession, currentUserId: string): string {
  return session.callerId === currentUserId ? session.calleeId : session.callerId
}

function showTransferNotice(message: string): void {
  patchVoiceCallSession({ notice: message })
  window.setTimeout(() => {
    const current = getVoiceCallSession()
    if (current?.notice === message) patchVoiceCallSession({ notice: undefined })
  }, 4000)
}

export function useCallTransfer(currentUserId: string | undefined) {
  const { completeBlindTransfer } = useVoiceCall()
  const [sheetOpen, setSheetOpen] = useState(false)
  const [warnOpen, setWarnOpen] = useState(false)
  const [pending, setPending] = useState<{ target: ChatUser; mode: CallTransferMode } | null>(null)
  const [eligibility, setEligibility] = useState<TransferEligibilityResult | null>(null)
  const [bookingCallback, setBookingCallback] = useState(false)

  const resetWarn = useCallback(() => {
    setWarnOpen(false)
    setPending(null)
    setEligibility(null)
  }, [])

  const executeTransfer = useCallback(async (target: ChatUser, mode: CallTransferMode) => {
    const session = getVoiceCallSession()
    if (!session || !currentUserId) return

    try {
      // TRUE blind transfer: the controller tells the held party to re-dial the
      // target as a fresh 1:1 and drops us off. No hold music, no group bridge.
      await completeBlindTransfer(target)
    } catch {
      patchVoiceCallSession({ transfer: { phase: 'failed', mode } })
      showTransferNotice('Transfer signal failed — try again.')
    }
  }, [currentUserId, completeBlindTransfer])

  const bookCallback = useCallback(async (target: ChatUser) => {
    const session = getVoiceCallSession()
    if (!session || !currentUserId) return

    setBookingCallback(true)
    try {
      let locationSnap = null
      try {
        locationSnap = await getLocationProvider().getCurrentPosition()
      } catch {
        /* optional safety snap */
      }

      const memberUserId = callPeerUserId(session, currentUserId)
      await bookCallbackSession({
        providerUserId: target.userId,
        memberUserId,
        callId: session.callId,
        clientRequestId: `${session.callId}-${target.userId}-${memberUserId}`,
        locationSnap,
        notes: `Callback booked during call transfer (${session.callId}).`,
      })
      showTransferNotice(`Callback booked for ${target.displayName}.`)
    } catch (e) {
      showTransferNotice(
        e instanceof Error ? e.message : 'Could not book callback session.',
      )
    } finally {
      setBookingCallback(false)
    }
  }, [currentUserId])

  const onTargetSelected = useCallback(
    (target: ChatUser, mode: CallTransferMode) => {
      setSheetOpen(false)
      const ctx = buildTransferTargetContext(target)
      const result = evaluateTransferEligibility(ctx, getTransferEligibilityConfig())
      if (result.issues.length === 0) {
        void executeTransfer(target, mode)
        return
      }
      setPending({ target, mode })
      setEligibility(result)
      setWarnOpen(true)
    },
    [executeTransfer],
  )

  const onWarnChoice = useCallback(
    async (choice: TransferEligibilityChoice) => {
      if (!pending) {
        resetWarn()
        return
      }
      if (choice === 'cancel') {
        resetWarn()
        return
      }
      if (choice === 'book_callback') {
        await bookCallback(pending.target)
        resetWarn()
        return
      }
      await executeTransfer(pending.target, pending.mode)
      resetWarn()
    },
    [pending, resetWarn, executeTransfer, bookCallback],
  )

  return {
    sheetOpen,
    setSheetOpen,
    warnOpen,
    eligibility,
    pendingTarget: pending?.target ?? null,
    bookingCallback,
    onTargetSelected,
    onWarnChoice,
    resetWarn,
  }
}
